import React, { useState, useEffect } from 'react';
import { Upload, FileText, Bot, Target, Check, Loader, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const HeroVisual: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [scores, setScores] = useState({ skills: 0, experience: 0, education: 0, overall: 0 });
  const [isAnimating, setIsAnimating] = useState(true);

  const steps = [
    { 
      icon: Upload, 
      title: t('landing.hero_visual.steps.cv_upload'), 
      color: 'from-blue-500 to-cyan-600',
      description: t('landing.hero_visual.descriptions.content_extraction'),
      duration: 3000
    },
    { 
      icon: Bot, 
      title: t('landing.hero_visual.steps.ai_analysis'), 
      color: 'from-purple-500 to-pink-600',
      description: t('landing.hero_visual.descriptions.semantic_analysis'),
      duration: 4000
    },
    { 
      icon: Target, 
      title: t('landing.hero_visual.steps.compatibility_calculation'), 
      color: 'from-emerald-500 to-teal-600',
      description: t('landing.hero_visual.descriptions.score_calculation'),
      duration: 3000
    },
    { 
      icon: Check, 
      title: t('landing.hero_visual.steps.results_ready'), 
      color: 'from-green-500 to-emerald-600',
      description: t('landing.hero_visual.descriptions.analysis_complete'),
      duration: 5000
    }
  ];

  useEffect(() => {
    if (!isAnimating) return;

    const runAnimation = () => {
      let stepIndex = 0;
      
      const nextStep = () => {
        if (stepIndex < steps.length) {
          setCurrentStep(stepIndex);
          
          setTimeout(() => {
            stepIndex++;
            if (stepIndex < steps.length) {
              nextStep();
            } else {
              // Pause on results, then restart
              setTimeout(() => {
                setScores({ skills: 0, experience: 0, education: 0, overall: 0 });
                stepIndex = 0;
                nextStep();
              }, 6000);
            }
          }, steps[stepIndex]?.duration || 3000);
        }
      };
      
      nextStep();
    };

    runAnimation();
  }, [isAnimating]);

  // Animate scores when reaching final step
  useEffect(() => {
    if (currentStep === 3) {
      const animateScores = () => {
        // Animate overall score first with slower progression
        let overallCount = 0;
        const overallInterval = setInterval(() => {
          overallCount += 1;
          setScores(prev => ({ ...prev, overall: Math.min(overallCount, 87) }));
          if (overallCount >= 87) clearInterval(overallInterval);
        }, 50);

        // Then animate individual scores with longer delays
        setTimeout(() => {
          let skillsCount = 0;
          const skillsInterval = setInterval(() => {
            skillsCount += 2;
            setScores(prev => ({ ...prev, skills: Math.min(skillsCount, 92) }));
            if (skillsCount >= 92) clearInterval(skillsInterval);
          }, 40);
        }, 1000);

        setTimeout(() => {
          let expCount = 0;
          const expInterval = setInterval(() => {
            expCount += 1;
            setScores(prev => ({ ...prev, experience: Math.min(expCount, 78) }));
            if (expCount >= 78) clearInterval(expInterval);
          }, 45);
        }, 1500);

        setTimeout(() => {
          let eduCount = 0;
          const eduInterval = setInterval(() => {
            eduCount += 2;
            setScores(prev => ({ ...prev, education: Math.min(eduCount, 85) }));
            if (eduCount >= 85) clearInterval(eduInterval);
          }, 35);
        }, 2000);
      };

      animateScores();
    }
  }, [currentStep]);

  const renderStepContent = () => {
    const step = steps[currentStep];
    const IconComponent = step.icon;

    if (currentStep < 3) {
      return (
        <div key="progress-state" className="text-center">
          <div className="relative mb-8">
            <div className={`w-24 h-24 mx-auto bg-gradient-to-br ${step.color} rounded-3xl flex items-center justify-center shadow-2xl`}>
              <IconComponent className="w-12 h-12 text-white" />
            </div>
            {/* Pulsing ring effect */}
            <div className={`absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-br ${step.color} rounded-3xl opacity-30 animate-ping`}></div>
            {/* Rotating loader */}
            <div className="absolute -inset-2 w-28 h-28 mx-auto border-4 border-transparent border-t-white/30 rounded-full animate-spin"></div>
          </div>
          
          <h3 className="text-2xl font-bold text-white mb-4">
            {step.title}
          </h3>
          
          {/* Enhanced loading bar */}
          <div className="w-full bg-slate-700/50 rounded-full h-3 mb-6 overflow-hidden">
            <div className={`bg-gradient-to-r ${step.color} h-3 rounded-full relative overflow-hidden`} style={{ width: '75%' }}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
            </div>
          </div>
          
          <p className="text-slate-300 text-lg mb-4">
            {step.description}
          </p>
          
          {/* Floating particles effect */}
          <div className="flex justify-center space-x-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`w-2 h-2 bg-gradient-to-r ${step.color} rounded-full animate-bounce`} style={{ animationDelay: `${i * 0.2}s` }}></div>
            ))}
          </div>
        </div>
      );
    }

    // Final results view
    return (
      <div key="results-state" className="text-center animate-fade-in">
        <div className="relative mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl">
            <Sparkles className="w-12 h-12 text-white animate-pulse" />
          </div>
          {/* Success glow effect */}
          <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-3xl opacity-20 animate-pulse"></div>
        </div>
        
        <h3 className="text-2xl font-bold text-white mb-4">
          {t('landing.hero_visual.compatibility_score')}
        </h3>
        
        <div className="text-7xl font-black bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-6 animate-pulse">
          {scores.overall}%
        </div>
        
        <p className="text-green-400 font-semibold text-lg mb-8">
          {t('landing.hero_visual.excellent_match')}
        </p>
        
        {/* Enhanced animated progress bars */}
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">{t('landing.hero_visual.skills')}</span>
            <span className="text-white font-semibold">{scores.skills}%</span>
          </div>
          <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-green-400 to-emerald-500 h-3 rounded-full transition-all duration-1000 relative overflow-hidden"
              style={{ width: `${scores.skills}%`, boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">{t('landing.hero_visual.experience')}</span>
            <span className="text-white font-semibold">{scores.experience}%</span>
          </div>
          <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all duration-1000 relative overflow-hidden"
              style={{ width: `${scores.experience}%`, boxShadow: '0 0 10px rgba(251, 191, 36, 0.5)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">{t('landing.hero_visual.education')}</span>
            <span className="text-white font-semibold">{scores.education}%</span>
          </div>
          <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-400 to-indigo-500 h-3 rounded-full transition-all duration-1000 relative overflow-hidden"
              style={{ width: `${scores.education}%`, boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
      {/* Floating background elements */}
      <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-full animate-float"></div>
      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full animate-float-delayed"></div>
      
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-500 group hover:scale-105 min-h-[500px] flex items-center">
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="w-full relative">
          {renderStepContent()}
        </div>
      </div>
      
      {/* Removed step indicators as requested */}
    </div>
  );
};

export default HeroVisual;